import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { eanConfigApi } from '../services/api';

const { Title } = Typography;

const EANConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();

  // 获取EAN配置列表
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await eanConfigApi.getConfigs();
      setConfigs(response.items || []);
    } catch (error) {
      message.error('获取EAN配置失败');
      console.error('获取EAN配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // 创建或更新配置
  const handleSubmit = async (values) => {
    try {
      if (editingConfig) {
        await eanConfigApi.updateConfig(editingConfig.id, values);
        message.success('EAN配置更新成功');
      } else {
        await eanConfigApi.createConfig(values);
        message.success('EAN配置创建成功');
      }
      setModalVisible(false);
      setEditingConfig(null);
      form.resetFields();
      fetchConfigs();
    } catch (error) {
      message.error(editingConfig ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 删除配置
  const handleDelete = async (id) => {
    try {
      console.log('开始删除配置，ID:', id);
      const result = await eanConfigApi.deleteConfig(id);
      console.log('删除结果:', result);
      message.success('删除成功');
      fetchConfigs();
    } catch (error) {
      console.error('删除失败详细信息:', {
        error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // 特殊处理删除操作的网络错误
      if (error.message === 'DELETE_NETWORK_ERROR') {
        console.log('删除操作可能已成功，刷新列表');
        message.success('删除成功');
        fetchConfigs();
      } else {
        message.error(`删除失败: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // 激活配置
  const handleActivate = async (id) => {
    try {
      await eanConfigApi.activateConfig(id);
      message.success('激活成功');
      fetchConfigs();
    } catch (error) {
      message.error('激活失败');
      console.error('激活失败:', error);
    }
  };

  // 打开编辑模态框
  const showEditModal = (config) => {
    setEditingConfig(config);
    form.setFieldsValue(config);
    setModalVisible(true);
  };

  // 打开创建模态框
  const showCreateModal = () => {
    setEditingConfig(null);
    form.resetFields();
    setModalVisible(true);
  };

  const columns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '国家码',
      dataIndex: 'country_code',
      key: 'country_code',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '厂商码',
      dataIndex: 'factory_code',
      key: 'factory_code',
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: '商品码起始',
      dataIndex: 'product_code_start',
      key: 'product_code_start',
      render: (text) => <Tag color="orange">{text}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '激活' : '未激活'}
        </Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.is_active && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleActivate(record.id)}
            >
              激活
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个配置吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={3}>EAN配置管理</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showCreateModal}
            >
              新建配置
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={configs}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />

        <Modal
          title={editingConfig ? '编辑EAN配置' : '新建EAN配置'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingConfig(null);
            form.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              is_active: true,
            }}
          >
            <Form.Item
              name="name"
              label="配置名称"
              rules={[{ required: true, message: '请输入配置名称' }]}
            >
              <Input placeholder="请输入配置名称" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="country_code"
                  label="国家码"
                  rules={[
                    { required: true, message: '请输入国家码' },
                    { len: 3, message: '国家码必须是3位数字' },
                    { pattern: /^\d{3}$/, message: '国家码必须是3位数字' },
                  ]}
                >
                  <Input placeholder="901" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="factory_code"
                  label="厂商码"
                  rules={[
                    { required: true, message: '请输入厂商码' },
                    { len: 4, message: '厂商码必须是4位数字' },
                    { pattern: /^\d{4}$/, message: '厂商码必须是4位数字' },
                  ]}
                >
                  <Input placeholder="3222" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="product_code_start"
                  label="商品码起始"
                  rules={[
                    { required: true, message: '请输入商品码起始' },
                    { len: 5, message: '商品码起始必须是5位数字' },
                    { pattern: /^\d{5}$/, message: '商品码起始必须是5位数字' },
                  ]}
                >
                  <Input placeholder="10000" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <Input.TextArea rows={3} placeholder="请输入配置描述" />
            </Form.Item>

            <Form.Item name="is_active" label="是否激活" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingConfig ? '更新' : '创建'}
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    setEditingConfig(null);
                    form.resetFields();
                  }}
                >
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default EANConfig; 